// Контекст ERP «Бетон»: API, реальное время, текущий пользователь, справочники «под рукой».

import { createContext, useContext } from "react";
import { Api } from "../shared/api";
import { Live } from "../shared/live";
import { OfflineQueue } from "../shared/offline";

export const api = new Api("beton");
export const live = new Live(api);
export const queue = new OfflineQueue(api, "beton");

export type Role = "manager" | "dispatcher" | "operator" | "director" | "admin" | "lab" | "driver";

export type Me = {
  id: string;
  login: string;
  full_name: string;
  role: Role;
  plant_ids: string[];
  all_plants: boolean;
  is_senior?: boolean;
  settings: { timezone: string; lateness_tolerance_minutes: number; lateness_reference: string };
};

export type Ref = { id: string; name: string };
export type Unit = { id: string; name: string; plant_id: string; plant_name: string; active: boolean; load_minutes: number };
export type Refs = {
  plants: Ref[];
  units: Unit[];
  grades: { id: string; code: string; requires_lab_approval: boolean }[];
  delayReasons: { id: string; name: string; side: string }[];
};

export const Ctx = createContext<{ me: Me; refs: Refs; reloadRefs: () => void }>(null as never);
export const useCtx = () => useContext(Ctx);

export const can = {
  createOrder: (r: Role) => ["manager", "dispatcher", "admin"].includes(r),
  dispatch: (r: Role) => ["dispatcher", "admin"].includes(r),
  plan: (r: Role) => ["manager", "director", "admin"].includes(r),
  admin: (r: Role) => r === "admin",
  lab: (r: Role) => ["lab", "admin", "director"].includes(r),
};

export const ORDER_STATUS: Record<string, string> = {
  draft: "Черновик", new: "Новая", confirmed: "Подтверждена", in_progress: "В работе", done: "Выполнена",
  partial: "Частично выполнена", rescheduled: "Перенесена", cancelled: "Отменена",
};
export const ORDER_TONE: Record<string, string> = {
  new: "s-planned", confirmed: "s-planned", in_progress: "s-loading", done: "s-done", partial: "s-transit",
  rescheduled: "", cancelled: "s-conflict", draft: "",
};

export const TRIP_STATUS: Record<string, string> = {
  planned: "Запланирован", assigned: "Машина назначена", queued: "В очереди", loading: "Погрузка",
  departed: "Выехал", on_site: "На объекте", unloading: "Разгрузка", returning: "Возврат",
  completed: "Завершён", cancelled: "Отменён",
};
export const TRIP_ORDER = ["planned", "assigned", "queued", "loading", "departed", "on_site", "unloading", "returning", "completed"];

/** Цвет блока рейса на графике — как на макете Э-2. */
export function tripSwatch(status: string, conflict: boolean): string {
  if (conflict) return "sw-conflict";
  if (status === "completed" || status === "returning" || status === "unloading") return "sw-done";
  if (status === "departed" || status === "on_site") return "sw-transit";
  if (status === "loading" || status === "queued") return "sw-loading";
  if (status === "cancelled") return "sw-none";
  return "sw-planned";
}

export const TRIP_LEGEND = [
  { cls: "sw-done", label: "Завершён" },
  { cls: "sw-transit", label: "В пути" },
  { cls: "sw-loading", label: "Погрузка" },
  { cls: "sw-planned", label: "Запланирован" },
  { cls: "sw-conflict", label: "Конфликт" },
];

export const CHANNELS: Record<string, string> = {
  manager_phone: "Телефон менеджера", dispatch_phone: "Телефон диспетчерской", email: "Почта", in_person: "Лично",
  repeat: "Повтор", recurring: "Регулярная", one_c: "1С",
};
export const POUR: Record<string, string> = { pump: "Насос", chute: "Лоток", bucket: "Бадья" };
export const ROLES: Record<string, string> = {
  manager: "Менеджер", dispatcher: "Диспетчер-логист", operator: "Оператор РБУ", director: "Руководитель",
  admin: "Администратор", lab: "Лаборант", driver: "Водитель",
};
